import React, { useContext } from 'react';
import { useTranslation } from 'Shared/contexts/TranslationsContext';
import classnames from 'classnames';

// Components
import { WpIcon } from 'Wpds/components/icon';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Hooks
import { handleLaunchExternalLink } from 'Shared/utils/external-link';
import { useApp } from 'Shared/utils/hooks/use-app';
import { useMarketingUrl } from 'Shared/utils/hooks/use-marketing-url';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { usePITracking } from 'Shared/tracking/use-pi-tracking';
import { useSmac } from 'Shared/utils/hooks/use-smac';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Context
import ExpandNavbarContext from 'Shared/contexts/ExpandNavbarContext';

// Styles
import './PILinkHeader.scss';

/**
 * A component which contains the link to PI from PI Perform built for the
 * SideNavigation component.
 */
function PILinkHeader() {
  const isExpanded = useContext(ExpandNavbarContext);
  const { piAppUrl } = useApp();
  const { isPiOrganization } = useOrganization();
  const { PISoftwareMarketingUrl } = useMarketingUrl();
  const { hasAccessToNonPerformModules } = useSmac();
  const { isThirdPartyUser: currentUserIsThirdParty } = useCurrentUser();

  const { trackLinkToPIClick } = usePITracking();
  const { tr } = useTranslation();

  // Check if the user has access to other modules, and if so, set the url to the PI app
  const url = isPiOrganization && hasAccessToNonPerformModules ? piAppUrl : PISoftwareMarketingUrl;

  const baseClassName = 'PILinkHeader';
  const linkClasses = classnames(baseClassName, 'border-bottom', {
    [`${baseClassName}--expanded`]: isExpanded,
  });

  const piText = isExpanded ? (
    <span className="position-relative">{tr('perform.side_navigation.pi_full_name')}</span>
  ) : null;

  const handlePILinkHeaderClick = () => {
    trackLinkToPIClick({ source: 'Navigation Menu Header' });
    handleLaunchExternalLink({ href: url });
  };

  if (currentUserIsThirdParty) return null;

  return (
    <WpTooltip placement="right">
      <WpTooltipTrigger>
        <div className={linkClasses} role="button" onClick={handlePILinkHeaderClick}>
          <div className={`${baseClassName}__nav-icon position-relative`}>
            <WpIcon name="external-link" />
          </div>
          {piText}
        </div>
      </WpTooltipTrigger>
      {!isExpanded && <WpTooltipContent>{tr('perform.side_navigation.pi_software_tooltip')}</WpTooltipContent>}
    </WpTooltip>
  );
}

export default React.memo(PILinkHeader);
